import type { FC } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import type {
  FlavorFinderDataType,
  FlavorFinderQuestions,
  FlavorFinderQuizResultsAnswersPayload,
  FlavorFinderResult,
  Step,
  ViewType,
} from "../../types/FlavorFinderTypes";

export interface FlavorFinderContext {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  previousStep: number[];
  setPreviousStep: React.Dispatch<React.SetStateAction<number[]>>;
  nextStep: number;
  setNextStep: React.Dispatch<React.SetStateAction<number>>;
  answers: number[];
  setAnswers: React.Dispatch<React.SetStateAction<number[]>>;
  questions: FlavorFinderQuestions[];
  selectedAnswersPayload: FlavorFinderQuizResultsAnswersPayload[];
  setSelectedAnswersPayload: React.Dispatch<React.SetStateAction<FlavorFinderQuizResultsAnswersPayload[]>>;
  userJourney: Step[];
  setUserJourney: React.Dispatch<React.SetStateAction<Step[]>>;
  currentView: ViewType;
  setCurrentView: React.Dispatch<React.SetStateAction<ViewType>>;
  flavorFinderData: FlavorFinderDataType;
  currentQuestion: FlavorFinderQuestions | null;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<FlavorFinderQuestions | null>>;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  quizResult: FlavorFinderResult;
  setQuizResult: React.Dispatch<React.SetStateAction<FlavorFinderResult>>;
  startQuiz: () => void;
  resetQuiz: () => void;
  handlePreviousStepClick: () => void;
}

interface FlavorFinderContextProviderProps {
  children: React.ReactNode;
  data: FlavorFinderDataType;
}

export function getFlavorFinderFirstQuestion(quizData: FlavorFinderDataType): FlavorFinderQuestions | undefined {
  return quizData.quizTransitions.find((question) => {
    if (quizData.quizId) {
      return question?.quizId === quizData.quizId && !question.onAnswerId;
    }
    return !question.onAnswerId;
  });
}

export const FlavorFinderContext = createContext<FlavorFinderContext>(undefined!);
export const useFlavorFinderContext = () => useContext(FlavorFinderContext);

export const FlavorFinderContextProvider: FC<FlavorFinderContextProviderProps> = ({ children, data }) => {
  const [previousStep, setPreviousStep] = useState([-1]);
  const [nextStep, setNextStep] = useState(0);
  const [activeStep, setActiveStep] = useState(-1);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selectedAnswersPayload, setSelectedAnswersPayload] = useState<FlavorFinderQuizResultsAnswersPayload[]>([]);
  const [userJourney, setUserJourney] = useState<Step[]>([{ view: "intro" }]);
  const [currentView, setCurrentView] = useState<ViewType>("intro");
  const [currentQuestion, setCurrentQuestion] = useState<FlavorFinderQuestions | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
  const flavorFinderData = data;
  const [quizResult, setQuizResult] = useState<FlavorFinderResult>({
    resultsLink: "",
    results: [],
    headerUrl: "",
    resultsLinkWithDevice: "",
  });

  const startQuiz = () => {
    const firstQuestion = getFlavorFinderFirstQuestion(flavorFinderData);
    if (firstQuestion) {
      setCurrentQuestion(firstQuestion);
      setUserJourney([...userJourney, { view: "question", question: firstQuestion }]);
      setCurrentQuestionIndex(0);
      setCurrentView("question");
    } else {
      console.error("Couldn't find the first question!");
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(null);
    setUserJourney([{ view: "intro" }]);
    setCurrentQuestionIndex(0);
    setCurrentView("intro");
  };

  const handlePreviousStepClick = useCallback(() => {
    const updatedJourney = [...userJourney];

    updatedJourney.pop();

    setUserJourney(updatedJourney);
    setCurrentQuestionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));

    const lastStep = updatedJourney[updatedJourney.length - 1];

    if (lastStep) {
      setCurrentView(lastStep.view);
      if (lastStep.view === "question" && lastStep.question) {
        setCurrentQuestion(lastStep.question);
      }
    }
  }, [setCurrentQuestion, setCurrentQuestionIndex, setCurrentView, setUserJourney, userJourney]);

  const questions = flavorFinderData.quizTransitions;
  const value = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      nextStep,
      setNextStep,
      previousStep,
      setPreviousStep,
      answers,
      setAnswers,
      questions,
      selectedAnswersPayload,
      setSelectedAnswersPayload,
      userJourney,
      setUserJourney,
      currentView,
      setCurrentView,
      flavorFinderData,
      startQuiz,
      resetQuiz,
      currentQuestion,
      setCurrentQuestion,
      currentQuestionIndex,
      setCurrentQuestionIndex,
      quizResult,
      setQuizResult,
      handlePreviousStepClick,
    }),
    [
      activeStep,
      answers,
      currentQuestion,
      currentQuestionIndex,
      currentView,
      flavorFinderData,
      handlePreviousStepClick,
      nextStep,
      previousStep,
      questions,
      quizResult,
      selectedAnswersPayload,
      startQuiz,
      userJourney,
    ]
  );
  return <FlavorFinderContext.Provider value={value}>{children}</FlavorFinderContext.Provider>;
};
